import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { useDateFilter } from '../contexts/DateFilterContext';
import { Landmark, CreditCard, FileText, Calculator, CheckCircle2, ArrowRight, AlertTriangle, Info, Copy, HelpCircle, Ban, CalendarX, Scale, Hash, Hourglass } from 'lucide-react';
import { tr } from '../lib/i18nHelpers';
import { stageOf } from '../lib/lifecycleStepper';
import Tooltip from '../components/Tooltip';
import LifecycleStepper from '../components/LifecycleStepper';

interface JeLine { code: string; name: string; debit: number; credit: number }
/** Canonical block from GET /review-queue: { type, id, stage, stage_since, flags }. */
interface LifecycleBlock {
  type: string;
  id: string;
  stage: string;
  stage_since: string | null;
  flags: string[];
}
interface QueueItem {
  type: 'bank_statement' | 'card_statement' | 'invoice' | 'receipt' | 'journal_entry';
  id: string;
  title: string;
  subtitle?: string;
  date: string;
  reason: string;
  reviewUrl: string;
  flags?: string;
  lifecycle?: LifecycleBlock;
  // journal-entry items (2026-10-14 redesign): where the money sits
  lines?: JeLine[];
}

interface QueueData {
  total: number;
  counts: {
    bank_statements: number;
    card_statements: number;
    invoices: number;
    journal_entries: number;
  };
  items: QueueItem[];
  items_total?: number;
  truncated?: boolean;
  // 2026-11-04 consultant additions: temp-account worklist + matching alert
  temp_items?: TempItem[];
  temp_items_total?: number;
  matching_never_run?: boolean;
}

/** A temp-parked voucher joined back to its source row (bank tx / invoice). */
interface TempItem {
  kind: 'bank_tx' | 'invoice' | 'journal';
  id: string;
  entry_id: string;
  entry_number: string;
  title: string;
  subtitle?: string;
  date: string;
  amount: number | null;
  temp_account: { code: string; name: string } | null;
  lines: JeLine[];
  navUrl: string;
}

// Exported for the Review upload subpage (UploadReview.tsx), which renders the
// same badge vocabulary/type chips — one source of truth for the flag texts.
export const TYPE_CONFIG: Record<string, { icon: React.FC<{ className?: string }>; color: string; label: string; labelZh: string; labelCn: string }> = {
  bank_statement: { icon: Landmark, color: 'text-blue-600', label: 'Bank Statements', labelZh: '銀行月結單', labelCn: '银行月结单' },
  card_statement: { icon: CreditCard, color: 'text-purple-600', label: 'Card Statements', labelZh: '信用卡月結單', labelCn: '信用卡月结单' },
  invoice: { icon: FileText, color: 'text-green-900 dark:text-green-200', label: 'Invoices', labelZh: '發票', labelCn: '发票' },
  receipt: { icon: FileText, color: 'text-teal-600', label: 'Receipts', labelZh: '收據', labelCn: '收据' },
  journal_entry: { icon: Calculator, color: 'text-amber-900 dark:text-amber-100', label: 'Journal Entries', labelZh: '日記帳分錄', labelCn: '日记账分录' },
};

const money = (n: number | null | undefined) =>
  (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Plain-language explanation for each review flag, shown on badge hover. */
function badgeTip(flag: string): string | null {
  switch (flag) {
    case 'draft':
      return tr(
        'Booked as a draft journal entry. It counts in the All-entries report view, but not in Confirmed postings until someone confirms it.',
        '草稿日記帳分錄：計入全部分錄報表視圖，未確認前不計入已確認過賬。',
        '草稿日记账分录：计入全部分录报表视图，未确认前不计入已确认过账。'
      );
    case 'balance_mismatch':
      return tr(
        "The statement's transactions don't add up to its printed closing balance. Check for missing or misread lines before confirming.",
        '月結單交易加總與列印的期末餘額不符，確認前請檢查有否漏讀或誤讀的行。',
        '月结单交易加总与列印的期末余额不符，确认前请检查有否漏读或误读的行。'
      );
    case 'direction':
      return tr(
        'The system could not tell whether the money was coming in or going out. Please verify before confirming.',
        '系統無法判斷資金是流入還是流出，確認前請人工核實。',
        '系统无法判断资金是流入还是流出，确认前请人工核实。'
      );
    case 'company_not_detected':
      return tr(
        'The counterparty company could not be identified from the document. Link or create it before confirming.',
        '無法從文件識別交易對手公司，確認前請連結或建立。',
        '无法从文件识别交易对手公司，确认前请连结或建立。'
      );
    case 'duplicate':
      return tr(
        'Looks like a repeat of a document already in the system. Verify it is genuinely new before confirming.',
        '疑似與系統內已有的單據重複，確認前請核實是否真的新單據。',
        '疑似与系统内已有的单据重复，确认前请核实是否真的新单据。'
      );
    case 'total':
      return tr(
        "The line items don't add up to the document's stated total. Please correct the amount before confirming.",
        '明細加總與單據所列總額不符，確認前請修正金額。',
        '明细加总与单据所列总额不符，确认前请修正金额。'
      );
    case 'pending_review':
      return tr(
        'Flagged for a human check before it can be booked.',
        '已標記需人工覆核後才入賬。',
        '已标记需人工复核后才入账。'
      );
    case 'new_company':
      return tr(
        'First time seeing this counterparty — confirm the company name is spelled correctly.',
        '首次出現的交易對手——請確認公司名稱正確。',
        '首次出现的交易对手——请确认公司名称正确。'
      );
    case 'temporary_ar_ap':
      return tr(
        'One side of this entry sits in a temporary parking account (11901 Temporary Receipt 暫收款 / 21901 Temporary Payment 暫付款). Reclassify it to the real account once known.',
        '分錄其中一方暫存於暫記科目（11901 暫收款／21901 暫付款），確定後請重新分類至真實科目。',
        '分录其中一方暂存于暂记科目（11901 暂收款／21901 暂付款），确定后请重新分类至真实科目。'
      );
    // 2026-10-22 spec §5.3 statement-continuity flags + §5.4 invoice number gaps
    case 'continuity_gap':
      return tr(
        'A month is missing from the statement chain for this account — no statement was received for that month.',
        '此帳戶的月結單鏈缺少一個月——該月未有收到月結單。',
        '此账户的月结单链缺少一个月——该月未收到月结单。'
      );
    case 'continuity_balance':
      return tr(
        'The closing balance of one statement does not match the opening balance of the next.',
        '上一張月結單的期末餘額與下一張的期初餘額不符。',
        '上一张月结单的期末余额与下一张的期初余额不符。'
      );
    case 'continuity_overlap':
      return tr(
        'This statement is out of month sequence with the previous one for the same account.',
        '此月結單的月份與同一帳戶上一張不按順序（月份重疊）。',
        '此月结单的月份与同一账户上一张不按顺序（月份重叠）。'
      );
    case 'continuity_date_overlap':
      return tr(
        'The printed periods of two consecutive statements overlap by more than a day.',
        '前後兩張月結單列印的結算期間重疊超過一天。',
        '前后两张月结单列印的结算期间重叠超过一天。'
      );
    case 'continuity_duplicate':
      return tr(
        'Two statements exist for the same account and month.',
        '同一帳戶同一月份有兩張月結單。',
        '同一账户同一月份有两张月结单。'
      );
    case 'invoice_number_gap':
      return tr(
        'The invoice number sequence jumps — numbers in between are unaccounted for.',
        '發票號碼出現跳號——中間的號碼未有紀錄。',
        '发票号码出现跳号——中间的号码未有记录。'
      );
    default:
      return null;
  }
}

export function ReasonBadge({ reason }: { reason: string }) {
  const reasons = reason.split(',').filter(Boolean);
  return (
    <span className="inline-flex flex-wrap gap-1">
      {reasons.map(r => {
        const flag = r.trim();
        switch (flag) {
          case 'draft': return <Badge key="draft" tip={badgeTip(flag)} className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full font-medium">Draft</Badge>;
          case 'balance_mismatch': return <Badge key="bm" tip={badgeTip(flag)} className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium inline-flex items-center gap-1"><AlertTriangle className="h-2.5 w-2.5" />Balance</Badge>;
          case 'direction': return <Badge key="dir" tip={badgeTip(flag)} className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium inline-flex items-center gap-1"><AlertTriangle className="h-2.5 w-2.5" />Direction</Badge>;
          case 'company_not_detected': return <Badge key="cnd" tip={badgeTip(flag)} className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium inline-flex items-center gap-1"><Info className="h-2.5 w-2.5" />Company</Badge>;
          case 'duplicate': return <Badge key="dup" tip={badgeTip(flag)} className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full font-medium inline-flex items-center gap-1"><Copy className="h-2.5 w-2.5" />Duplicate</Badge>;
          case 'total': return <Badge key="tot" tip={badgeTip(flag)} className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium">Total mismatch</Badge>;
          case 'pending_review': return <Badge key="pr" tip={badgeTip(flag)} className="text-[10px] bg-yellow-100 text-yellow-900 dark:text-yellow-200 px-1.5 py-0.5 rounded-full font-medium">Needs review</Badge>;
          case 'new_company': return <Badge key="nc" tip={badgeTip(flag)} className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">New company</Badge>;
          case 'temporary_ar_ap': return <Badge key="taa" tip={badgeTip(flag)} className="text-[10px] bg-amber-100 text-amber-900 dark:text-amber-100 px-1.5 py-0.5 rounded-full font-medium">Parked in temp</Badge>;
          // 2026-10-22 spec §5.3 continuity flags + §5.4 invoice number gaps (Task 10 surfaces these)
          case 'continuity_gap': return <Badge key="cgap" tip={badgeTip(flag)} className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium inline-flex items-center gap-1"><CalendarX className="h-2.5 w-2.5" />{tr('Statement gap', '月結單缺月', '月结单缺月')}</Badge>;
          case 'continuity_balance': return <Badge key="cbal" tip={badgeTip(flag)} className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium inline-flex items-center gap-1"><Scale className="h-2.5 w-2.5" />{tr('Balance chain break', '餘額鏈斷裂', '余额链断裂')}</Badge>;
          case 'continuity_overlap': return <Badge key="covl" tip={badgeTip(flag)} className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium inline-flex items-center gap-1"><CalendarX className="h-2.5 w-2.5" />{tr('Month overlap', '月份重疊', '月份重叠')}</Badge>;
          case 'continuity_date_overlap': return <Badge key="cdov" tip={badgeTip(flag)} className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium inline-flex items-center gap-1"><CalendarX className="h-2.5 w-2.5" />{tr('Period overlap', '期間重疊', '期间重叠')}</Badge>;
          case 'continuity_duplicate': return <Badge key="cdup" tip={badgeTip(flag)} className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full font-medium inline-flex items-center gap-1"><Copy className="h-2.5 w-2.5" />{tr('Duplicate statement', '重複月結單', '重复月结单')}</Badge>;
          case 'invoice_number_gap': return <Badge key="ingap" tip={badgeTip(flag)} className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium inline-flex items-center gap-1"><Hash className="h-2.5 w-2.5" />{tr('Invoice no. gap', '跳號', '跳号')}</Badge>;
          default: return <Badge key={flag} tip={badgeTip(flag)} className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full font-medium">{flag}</Badge>;
        }
      })}
    </span>
  );
}

/** A review-flag chip, optionally carrying a hover explanation. */
function Badge({ tip, className, children }: { tip: string | null; className: string; children?: React.ReactNode }) {
  if (!tip) return <span className={className}>{children}</span>;
  return (
    <Tooltip content={tip}>
      <span className={`${className} cursor-help`}>{children}</span>
    </Tooltip>
  );
}

/** Dr/Cr chips for one journal entry — the "where does the money sit" display. */
function JeLines({ lines }: { lines: JeLine[] }) {
  return (
    <span className="flex flex-wrap gap-1.5">
      {lines.map((l, i) => {
        const isDr = (l.debit || 0) > 0;
        return (
          <span key={i} className="inline-flex items-center gap-1 text-[10px] font-mono bg-muted/60 border rounded px-1.5 py-0.5">
            <span className={`font-bold ${isDr ? 'text-blue-700 dark:text-blue-300' : 'text-orange-700 dark:text-orange-300'}`}>{isDr ? 'Dr' : 'Cr'}</span>
            <span className="font-semibold">{l.code}</span>
            <span className="text-muted-foreground">{money(isDr ? l.debit : l.credit)}</span>
          </span>
        );
      })}
    </span>
  );
}

export function GroupHeader({ icon: Icon, color, title, count, tip }: {
  icon: React.FC<{ className?: string }>; color: string; title: string; count: number; tip: React.ReactNode;
}) {
  return (
    <div className="px-4 py-2.5 bg-muted/50 border-b flex items-center gap-2">
      <Icon className={`h-4 w-4 ${color}`} />
      <span className="font-semibold text-sm">{title}</span>
      <span className="text-xs text-muted-foreground">({count})</span>
      <Tooltip content={tip}>
        <span className="ml-1 text-muted-foreground cursor-help"><HelpCircle className="h-3.5 w-3.5" /></span>
      </Tooltip>
    </div>
  );
}

export default function ReviewQueue() {
  const { i18n } = useTranslation();

  const { startDate, endDate } = useDateFilter();

  const { data, isLoading } = useQuery<QueueData>({
    queryKey: ['review-queue', startDate, endDate],
    queryFn: () => {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      return api(`/review-queue?${params.toString()}`) as Promise<QueueData>;
    },
    refetchInterval: 5000,
  });

  const items = data?.items || [];
  const tempItems = data?.temp_items || [];
  const neverRun = data?.matching_never_run ?? false;

  // ── The one honest section (spec §4, Task 17; the 2026-10-14 "Parked in
  // temp" worklist was removed 2026-10-16) ──
  // "Blocked": source documents at needs_review ("pending verification") +
  // manual GJE vouchers at booked — NOTHING here counts in the books.
  // Posted auto-match vouchers with temp-account legs are hidden: the temp
  // side cancels itself out as soon as the bank ↔ invoice match is confirmed,
  // so there is nothing for an accountant to reclassify. A temp-parked voucher
  // still awaiting its author's confirm (stage 'booked') is a genuine block
  // and stays in the manual-voucher list below.
  const isTempParked = (i: QueueItem) =>
    (i.lifecycle?.flags ?? []).includes('temporary_ar_ap') || i.reason === 'temporary_ar_ap';
  const blocked = items.filter(
    i => i.type !== 'journal_entry' || !isTempParked(i) || i.lifecycle?.stage === 'booked',
  );
  const blockedDocs = blocked.filter(i => i.type !== 'journal_entry');
  const blockedVouchers = blocked.filter(i => i.type === 'journal_entry');

  const renderDocItem = (item: QueueItem) => (
    <a key={item.id} href={item.reviewUrl}
      className="flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors group">
      <div className="space-y-0.5 min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium truncate">{item.title}</span>
          <ReasonBadge reason={item.reason} />
        </div>
        {item.subtitle && <p className="text-xs text-muted-foreground truncate">{item.subtitle}</p>}
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <span>{item.date}</span>
          <LifecycleStepper
            type="document"
            stage={stageOf('document', { lifecycle: item.lifecycle })}
            stageSince={item.lifecycle?.stage_since ?? undefined}
            variant="compact"
          />
        </div>
      </div>
      <span className="text-muted-foreground group-hover:text-primary transition-colors shrink-0 ml-3">
        <ArrowRight className="h-4 w-4" />
      </span>
    </a>
  );

  const renderJeItem = (item: QueueItem) => (
    <a key={item.id} href={item.reviewUrl}
      className="flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors group">
      <div className="space-y-1 min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium font-mono">{item.title}</span>
          <ReasonBadge reason={item.reason} />
          {item.lines && item.lines.length > 0 && <JeLines lines={item.lines} />}
        </div>
        {item.subtitle && <p className="text-xs text-muted-foreground truncate">{item.subtitle}</p>}
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <span>{item.date}</span>
          <LifecycleStepper
            type="voucher"
            stage={stageOf('voucher', { lifecycle: item.lifecycle })}
            stageSince={item.lifecycle?.stage_since ?? undefined}
            variant="compact"
          />
        </div>
      </div>
      <span className="text-muted-foreground group-hover:text-primary transition-colors shrink-0 ml-3">
        <ArrowRight className="h-4 w-4" />
      </span>
    </a>
  );

  // ── Temp-account worklist rows (2026-11-04): navigate back to the source
  // subpage — bank statements (auto-expand + pulse) or AR/AP (auto-open the
  // detail modal). ──
  const TEMP_KIND_CONFIG: Record<TempItem['kind'], { icon: React.FC<{ className?: string }>; color: string; label: string; labelZh: string; labelCn: string }> = {
    bank_tx: { icon: Landmark, color: 'text-blue-600', label: 'Bank transaction', labelZh: '銀行交易', labelCn: '银行交易' },
    invoice: { icon: FileText, color: 'text-green-900 dark:text-green-200', label: 'Invoice', labelZh: '發票', labelCn: '发票' },
    journal: { icon: Calculator, color: 'text-amber-900 dark:text-amber-100', label: 'Journal entry', labelZh: '日記帳分錄', labelCn: '日记账分录' },
  };
  const renderTempItem = (item: TempItem) => {
    const cfg = TEMP_KIND_CONFIG[item.kind] || TEMP_KIND_CONFIG.journal;
    const Icon = cfg.icon;
    return (
      <a key={`${item.kind}-${item.id}`} href={item.navUrl}
        className="flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors group">
        <div className="space-y-1 min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Icon className={`h-3.5 w-3.5 shrink-0 ${cfg.color}`} />
            <span className="text-[10px] font-semibold text-muted-foreground">{tr(cfg.label, cfg.labelZh, cfg.labelCn)}</span>
            <span className="text-sm font-medium truncate max-w-[16rem]">{item.title}</span>
            {item.temp_account && (
              <span className="text-[10px] font-mono bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 px-1.5 py-0.5 rounded-full font-medium">
                {item.temp_account.code}
              </span>
            )}
            {item.lines.length > 0 && <JeLines lines={item.lines} />}
          </div>
          {item.subtitle && <p className="text-xs text-muted-foreground truncate">{item.subtitle}</p>}
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <span>{item.date}</span>
            {item.amount != null && item.amount > 0 && <span>· {money(item.amount)}</span>}
            <span>· {item.entry_number}</span>
          </div>
        </div>
        <span className="text-muted-foreground group-hover:text-primary transition-colors shrink-0 ml-3">
          <ArrowRight className="h-4 w-4" />
        </span>
      </a>
    );
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">{tr('Pending Review', '待審核', '待审核')}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {tr(
              'Blocked records only — documents awaiting review and manual journal entries awaiting confirmation. Temp-account vouchers resolve themselves once their invoice match is confirmed.',
              '僅顯示受阻記錄——待覆核的文件與待確認的手動日記帳分錄。暫記科目憑單會在配對發票確認後自動解決。',
              '仅显示受阻记录——待复核的文件与待确认的手动日记账分录。暂记科目凭证会在配对发票确认后自动解决。'
            )}
          </p>
        </div>
        <span className="text-xs text-muted-foreground">{tr('Auto-refreshes every 5s', '每5秒自動更新', '每5秒自动更新')}</span>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      ) : blocked.length === 0 && tempItems.length === 0 && !neverRun ? (
        <div className="bg-card border rounded-xl p-12 text-center space-y-3">
          <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto" />
          <p className="text-lg font-semibold">{tr('All caught up!', '全部已完成！', '全部已完成！')}</p>
          <p className="text-sm text-muted-foreground">{tr('No items pending review.', '沒有待審核的項目。', '没有待审核的项目。')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {data?.truncated && data.items_total != null && (
            <div className="text-xs text-amber-900 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/30 border border-amber-300 rounded px-3 py-2">
              {tr(
                `Showing the first ${items.length} of ${data.items_total} — narrow the date filter to see the rest.`,
                `僅顯示前 ${items.length} 項（共 ${data.items_total} 項）——縮小日期範圍以查看其餘項目。`,
                `仅显示前 ${items.length} 项（共 ${data.items_total} 项）——缩小日期范围以查看其余项目。`,
              )}
            </div>
          )}
          {/* ── Section 1: Blocked — nothing here is in the books ── */}
          {blocked.length > 0 && (
            <div className="bg-card border rounded-xl overflow-hidden">
              <GroupHeader icon={Ban} color="text-red-500"
                title={tr('Blocked', '被卡住', '被卡住')}
                count={blocked.length}
                tip={tr(
                  'Documents held for review plus manual journal entries awaiting confirmation. NOTHING in this section counts in the books — the figures appear in financial statements only after you confirm the review (or the booking).',
                  '待覆核的文件加上待確認的手動日記帳分錄。此區的任何項目都未入帳——覆核確認（或入賬確認）後才會計入財務報表。',
                  '待复核的文件加上待确认的手动日记账分录。此区的任何项目都未入账——复核确认（或入账确认）后才会计入财务报表。'
                )} />

              {/* Documents, sub-grouped by type */}
              {(() => {
                const grouped = new Map<string, QueueItem[]>();
                for (const item of blockedDocs) {
                  if (!grouped.has(item.type)) grouped.set(item.type, []);
                  grouped.get(item.type)!.push(item);
                }
                return Array.from(grouped.entries()).map(([type, typeItems]) => {
                  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.invoice;
                  const Icon = cfg.icon;
                  return (
                    <div key={type} className="border-t first:border-t-0">
                      <div className="px-4 pt-2 flex items-center gap-1.5">
                        <Icon className={`h-3.5 w-3.5 ${cfg.color}`} />
                        <span className="text-xs font-semibold">{tr(cfg.label, cfg.labelZh, cfg.labelCn)}</span>
                      </div>
                      <div className="divide-y">{typeItems.map(renderDocItem)}</div>
                    </div>
                  );
                });
              })()}

              {/* Manual journal entries awaiting their author's confirm */}
              {blockedVouchers.length > 0 && (
                <div className="border-t">
                  <div className="px-4 pt-2 flex items-center gap-1.5">
                    <Calculator className="h-3.5 w-3.5 text-amber-900 dark:text-amber-100" />
                    <span className="text-xs font-semibold">
                      {tr('Manual journal entries — awaiting confirmation', '手動日記帳分錄——待確認', '手动日记账分录——待确认')}
                    </span>
                  </div>
                  <div className="divide-y">{blockedVouchers.map(renderJeItem)}</div>
                </div>
              )}
            </div>
          )}

          {/* ── Matching never run (consultant 2026-11-04) ── */}
          {neverRun && (
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-300 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0 space-y-1">
                <p className="text-sm font-semibold">
                  {tr('Statement–invoice matching has never been run', '尚未執行月結單與發票配對', '尚未执行月结单与发票配对')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {tr(
                    'There are bank statements and invoices on the books, but no matching pass has been done yet. Run the matching so movements pair with their invoices — unmatched rows stay parked in Accounts Receivable / Accounts Payable until reviewed.',
                    '帳內有月結單與發票，但從未執行配對。請先執行配對讓款項與發票對應——未配對的往來會一直暫存於應收／應付帳。',
                    '账内有月结单与发票，但从未执行配对。请先执行配对让款项与发票对应——未配对的往来会一直暂存于应收／应付账。'
                  )}
                </p>
              </div>
              <a href="/bank-statements?filter=unmatched"
                className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-900 dark:text-amber-100 bg-amber-100 dark:bg-amber-900/40 border border-amber-300 rounded-lg px-3 py-2 hover:bg-amber-200 dark:hover:bg-amber-900/60 transition-colors">
                {tr('Go to matching', '前往配對', '前往配对')}
                <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </div>
          )}

          {/* ── Parked in Temporary accounts (consultant 2026-11-04) ── */}
          {tempItems.length > 0 && (
            <div className="bg-card border rounded-xl overflow-hidden">
              <GroupHeader icon={Hourglass} color="text-red-500"
                title={tr('Parked in Temporary accounts', '暫存於暫收／暫付帳', '暂存于暂收／暂付账')}
                count={tempItems.length}
                tip={tr(
                  'Bank transactions you explicitly skipped in match review ("no invoice for this") plus any vouchers still carrying Temporary Receipt/Payment legs. Open the source record to reclassify it or re-link it to an invoice.',
                  '您在配對覆核中明確跳過（「無對應發票」）的銀行交易，以及仍帶暫收／暫付帳一方的憑單。開啟來源記錄即可重新分類或重新配對發票。',
                  '您在配对复核中明确跳过（「无对应发票」）的银行交易，以及仍带暂收／暂付账一方的凭证。打开来源记录即可重新分类或重新配对发票。'
                )} />
              <div className="divide-y">{tempItems.map(renderTempItem)}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
